/**
 * FinList Launch Grace Period Utility
 * 
 * SAFE-MODE IMPLEMENTATION
 * This provides a time-based feature flag that temporarily grants full access
 * to all premium features until April 1, 2026.
 * 
 * After the grace period ends, the app automatically reverts to existing
 * Billing 2.0 behavior with zero extra work required.
 * 
 * NON-NEGOTIABLE RULES:
 * - This is a TOP-LEVEL OVERRIDE only
 * - Does NOT modify billing, trial, or subscription logic
 * - Does NOT change Stripe configuration
 * - Fully reversible by date (no cleanup needed)
 */

/**
 * Grace period end date: April 1, 2026 00:00:00 UTC
 * March 31, 2026 23:59:59 UTC is still within the grace period.
 */
export const GRACE_PERIOD_END_DATE = new Date('2026-04-01T00:00:00.000Z');

/**
 * Grace period start date (for display purposes)
 */
export const GRACE_PERIOD_START_DATE = new Date('2024-12-17T00:00:00.000Z');

/**
 * Check if the Launch Grace Period is currently active.
 * 
 * During the grace period (now → March 31, 2026 23:59:59 UTC):
 * - All premium features are free
 * - No payment prompts required
 * - Existing billing code remains intact
 * 
 * After the grace period (April 1, 2026 onwards):
 * - Returns false, allowing Billing 2.0 to take effect as-is
 * - Shopping List remains free for all users
 * 
 * @param {Date} [now=new Date()] - The current date/time (for testing)
 * @returns {boolean} - True if grace period is active, false otherwise
 */
export function isLaunchGracePeriodActive(now = new Date()) {
  return now.getTime() < GRACE_PERIOD_END_DATE.getTime();
}

/**
 * Get the number of days remaining in the grace period.
 * Returns 0 if grace period has ended.
 * 
 * @param {Date} [now=new Date()] - The current date/time
 * @returns {number} - Days remaining (0 if ended)
 */
export function getGracePeriodDaysRemaining(now = new Date()) {
  const msRemaining = GRACE_PERIOD_END_DATE.getTime() - now.getTime();
  if (msRemaining <= 0) return 0;
  return Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
}

/**
 * Get the grace period end date formatted for display.
 * 
 * @returns {string} - Formatted date string (e.g., "March 31, 2026")
 */
export function getGracePeriodEndDateFormatted() {
  // Display as March 31, 2026 since that's the last day of free access
  const lastFreeDay = new Date(GRACE_PERIOD_END_DATE.getTime() - 1); // March 31, 2026 23:59:59
  return lastFreeDay.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * Grace period configuration object for use in components.
 */
export const gracePeriodConfig = {
  endDate: GRACE_PERIOD_END_DATE,
  displayEndDate: 'March 31, 2026',
  message: '🎉 Launch Offer: All features are free until March 31, 2026!',
  shortMessage: 'Free until Mar 31, 2026',
  isActive: () => isLaunchGracePeriodActive(),
  daysRemaining: () => getGracePeriodDaysRemaining()
};
